import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

  }

  #controller = new AbortController();
  #selection = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles product selection using existing media before the section response.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    if (
      event.product?.id &&
      this.dataset.productId &&
      this.#resourceId(event.product.id) !== this.#resourceId(this.dataset.productId)
    )
      return;

    const selection = ++this.#selection;
    const handledLocally = this.#selectMedia(event.detail?.mediaId);

    event.promise
      .then(({ detail }) => {
        if (selection !== this.#selection || !this.isConnected || !detail?.html) return;
        if (!detail.newProduct) {
          const mediaId = detail.resource?.featured_media?.id;
          if (handledLocally || !mediaId || this.#selectMedia(mediaId)) return;
        }

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #selectMedia(mediaId) {
    if (!mediaId) return false;

    const selector = `.product-media[data-media-id="${CSS.escape(String(mediaId))}"]`;
    const selectedSlide = this.slideshow?.querySelector(selector)?.closest('slideshow-slide');
    const slides = this.slideshow?.refs.slides;
    const selectedIndex = selectedSlide && slides ? Array.from(slides).indexOf(selectedSlide) : -1;
    let selected = false;

    if (selectedIndex >= 0) {
      this.slideshow.select(selectedIndex, undefined, { animate: true, scrollThrough: true });
      selected = true;
    }

    const selectedGridItem = this.querySelector(`.media-gallery__grid ${selector}`)?.closest('li');
    if (selectedGridItem?.parentElement) {
      selectedGridItem.parentElement.prepend(selectedGridItem);
      selected = true;
    }

    return selected;
  }

  #resourceId(id) {
    return String(id).split('/').pop();
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
